/* eslint-disable react/no-unknown-property */
import { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Float } from '@react-three/drei';
import * as THREE from 'three';

const HolographicSpellCheckPanel = ({
  isVisible,
  original,
  corrected,
  correctionsSummary,
  onConfirm,
  onEdit,
  position = [0, 1.8, -2.5]
}) => {
  const panelRef = useRef();
  const glowRef = useRef();
  const [opacity, setOpacity] = useState(0);
  const particlesRef = useRef([]);

  useEffect(() => {
    if (isVisible) {
      // Animate in
      const timer = setInterval(() => {
        setOpacity(prev => {
          if (prev >= 1) {
            clearInterval(timer);
            return 1;
          }
          return prev + 0.1;
        });
      }, 30);
      return () => clearInterval(timer);
    } else {
      setOpacity(0);
    }
  }, [isVisible]);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (panelRef.current && isVisible) {
      // Gentle floating animation
      panelRef.current.position.y = position[1] + Math.sin(time * 1.5) * 0.02;
      panelRef.current.rotation.y = Math.sin(time * 0.5) * 0.02;
    }
    
    if (glowRef.current) {
      // Pulsing glow
      glowRef.current.material.opacity = 0.3 + Math.sin(time * 3) * 0.1;
    }
  });

  if (!isVisible) return null;

  return (
    <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
      <group ref={panelRef} position={position}>
        {/* Holographic frame glow */}
        <mesh ref={glowRef} position={[0, 0, -0.02]}>
          <planeGeometry args={[1.2, 0.9]} />
          <meshBasicMaterial 
            color="#BA7B7C" 
            transparent 
            opacity={0.3}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Main panel backing */}
        <mesh position={[0, 0, -0.01]}>
          <planeGeometry args={[1.1, 0.8]} />
          <meshPhysicalMaterial 
            color="#FFFFFF"
            transparent
            opacity={0.15 * opacity}
            roughness={0.1}
            metalness={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Holographic border lines */}
        <lineSegments position={[0, 0, 0]}>
          <edgesGeometry args={[new THREE.PlaneGeometry(1.1, 0.8)]} />
          <lineBasicMaterial color="#FFB6C1" transparent opacity={0.8 * opacity} />
        </lineSegments>

        {/* Corner accents */}
        {[[-0.52, 0.37], [0.52, 0.37], [-0.52, -0.37], [0.52, -0.37]].map((pos, i) => (
          <mesh key={`corner-${i}`} position={[pos[0], pos[1], 0.01]}>
            <circleGeometry args={[0.03, 16]} />
            <meshBasicMaterial color="#FFD4E5" transparent opacity={0.9 * opacity} />
          </mesh>
        ))}

        {/* Floating particles around the panel */}
        {[...Array(12)].map((_, i) => {
          const angle = (i / 12) * Math.PI * 2;
          const radius = 0.7;
          return (
            <mesh
              key={`particle-${i}`}
              position={[
                Math.cos(angle) * radius,
                Math.sin(angle) * radius * 0.6,
                0.05
              ]}
            >
              <sphereGeometry args={[0.01, 8, 8]} />
              <meshBasicMaterial color="#FFB6C1" transparent opacity={0.6 * opacity} />
            </mesh>
          );
        })}

        {/* HTML Content */}
        <Html
          transform
          occlude
          position={[0, 0, 0.02]}
          style={{
            opacity: opacity,
            transition: 'opacity 0.3s ease',
            pointerEvents: isVisible ? 'auto' : 'none'
          }}
        >
          <div 
            className="w-[400px] p-6 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(253,248,246,0.95) 100%)',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(186,123,124,0.3), inset 0 1px 0 rgba(255,255,255,0.8)',
              border: '1px solid rgba(186,123,124,0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-2xl">✨</span>
              <h3 
                className="text-xl font-medium text-[#BA7B7C]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                ¿Quisiste decir?
              </h3>
            </div>

            {correctionsSummary && (
              <p className="text-xs text-[#A8A29E] mb-4 italic">
                {correctionsSummary}
              </p>
            )}

            {/* Original - crossed out */}
            <div className="bg-red-50/80 rounded-xl p-3 mb-3 border border-red-100">
              <p className="text-xs text-red-400 mb-1 text-left">Original:</p>
              <p className="text-sm text-red-400 line-through text-left">{original}</p>
            </div>

            {/* Corrected - highlighted */}
            <div 
              className="rounded-xl p-3 mb-5 border border-[#BA7B7C]/30"
              style={{ background: 'linear-gradient(135deg, rgba(186,123,124,0.1) 0%, rgba(212,165,165,0.1) 100%)' }}
            >
              <p className="text-xs text-[#BA7B7C] mb-1 text-left">Sugerencia:</p>
              <p 
                className="text-sm text-[#1C1917] font-medium text-left"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                &ldquo;{corrected}&rdquo;
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => onConfirm?.(corrected)}
                className="flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-[#D4A5A5] to-[#BA7B7C] text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all transform hover:scale-105"
                style={{ boxShadow: '0 4px 15px rgba(186,123,124,0.4)' }}
              >
                <span>✓</span>
                <span>Sí, usar esta</span>
              </button>
              <button
                onClick={() => onEdit?.(corrected)}
                className="flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-white border-2 border-[#E7E5E4] text-[#57534E] rounded-xl text-sm font-medium hover:border-[#BA7B7C] hover:text-[#BA7B7C] transition-all"
              >
                <span>✏️</span>
                <span>Editar</span>
              </button>
            </div>
          </div>
        </Html>
      </group>
    </Float>
  );
};

export default HolographicSpellCheckPanel;
